import { addLog, toggleCheck, uid, type Assignment, type Contact, type Workspace } from "./workspace"

/**
 * Assignments: tasks handed to a person (usually someone in Contacts) so the incident record shows who is
 * doing what and when it was done. Every change is written to the Timeline with its time. The assignee does
 * not need an account: the person running the incident keeps the record.
 */
export interface AssignInput {
  title: string
  /** An existing contact. */
  contactId?: string | null
  /** Or someone new; they are added to Contacts so the two sections stay linked. */
  newPerson?: { name: string; role?: string; phone?: string }
  dueAt?: string
  note?: string
  /** The checklist item this task came from. */
  itemId?: string
}

const validIso = (s?: string) => (s && !Number.isNaN(new Date(s).getTime()) ? new Date(s).toISOString() : undefined)
export const contactName = (c: Contact) => c.name.trim() || c.role.trim() || "Someone"

export function assignTask(w: Workspace, input: AssignInput, now = new Date()): Workspace {
  const title = input.title.trim()
  if (!title) return w
  let contacts = w.contacts
  let contactId: string | null = null
  let assigneeName = ""
  const existing = input.contactId ? w.contacts.find((c) => c.id === input.contactId) : undefined
  if (existing) { contactId = existing.id; assigneeName = contactName(existing) }
  else if (input.newPerson?.name.trim()) {
    const c: Contact = { id: uid(), role: input.newPerson.role?.trim() || "Helper", name: input.newPerson.name.trim(), phone: input.newPerson.phone?.trim() ?? "", notes: "" }
    contacts = [...w.contacts, c]; contactId = c.id; assigneeName = c.name
  } else return w

  const a: Assignment = { id: uid(), title, contactId, assigneeName, status: "open", createdAt: now.toISOString(), dueAt: validIso(input.dueAt), note: input.note?.trim() ?? "", itemId: input.itemId }
  let next: Workspace = { ...w, contacts, assignments: [...w.assignments, a], updatedAt: now.toISOString() }
  if (input.itemId) {
    const prev = next.checks[input.itemId] ?? { done: false }
    next = { ...next, checks: { ...next.checks, [input.itemId]: { ...prev, owner: assigneeName } } }
  }
  return addLog(next, `Assigned to ${assigneeName}: ${title}.`, now)
}

const replace = (w: Workspace, id: string, fn: (a: Assignment) => Assignment, now: Date): Workspace =>
  ({ ...w, updatedAt: now.toISOString(), assignments: w.assignments.map((a) => (a.id === id ? fn(a) : a)) })

/** Mark a task done at the time it was actually done (`at` may be earlier than now). Also ticks its checklist item. */
export function completeAssignment(w: Workspace, id: string, at = new Date(), now = new Date()): Workspace {
  const a = w.assignments.find((x) => x.id === id)
  if (!a || a.status !== "open") return w
  let next = replace(w, id, (x) => ({ ...x, status: "done", doneAt: at.toISOString() }), now)
  if (a.itemId) next = toggleCheck(next, a.itemId, true, at)
  return addLog(next, `${a.assigneeName || "Someone"} completed: ${a.title}.`, at)
}

export function reopenAssignment(w: Workspace, id: string, now = new Date()): Workspace {
  const a = w.assignments.find((x) => x.id === id)
  if (!a || a.status === "open") return w
  let next = replace(w, id, (x) => ({ ...x, status: "open", doneAt: undefined }), now)
  if (a.itemId && a.status === "done") next = toggleCheck(next, a.itemId, false, now)
  return addLog(next, `Reopened: ${a.title}.`, now)
}

export function cancelAssignment(w: Workspace, id: string, now = new Date()): Workspace {
  const a = w.assignments.find((x) => x.id === id)
  if (!a || a.status !== "open") return w
  return addLog(replace(w, id, (x) => ({ ...x, status: "cancelled" }), now), `Cancelled: ${a.title} (was assigned to ${a.assigneeName || "someone"}).`, now)
}

/** When someone ticks a checklist item themselves, any open task for it is finished too (no extra Timeline noise). */
export function closeLinkedAssignments(w: Workspace, itemId: string, at = new Date()): Workspace {
  if (!w.assignments.some((a) => a.itemId === itemId && a.status === "open")) return w
  return { ...w, assignments: w.assignments.map((a) => (a.itemId === itemId && a.status === "open" ? { ...a, status: "done", doneAt: at.toISOString() } : a)) }
}

export const assignmentsForContact = (w: Workspace, contactId: string) => w.assignments.filter((a) => a.contactId === contactId)
export const assignmentsForItem = (w: Workspace, itemId: string) => w.assignments.filter((a) => a.itemId === itemId && a.status !== "cancelled")
export const openAssignments = (w: Workspace) => w.assignments.filter((a) => a.status === "open")
export const isOverdue = (a: Assignment, now = new Date()) => a.status === "open" && !!a.dueAt && new Date(a.dueAt).getTime() < now.getTime()

// ---- wiping a lost or stolen device ------------------------------------------------------------------
/** "I wiped it": records when it really happened. */
export function markWiped(w: Workspace, itemId: string, at = new Date(), now = new Date()): Workspace {
  let next = toggleCheck(w, itemId, true, at)
  next = closeLinkedAssignments(next, itemId, at)
  return addLog({ ...next, updatedAt: now.toISOString() }, "Remotely wiped the device.", at)
}

/** "I'm choosing not to wipe it": recorded with the reason, so the decision is on the record. */
export function declineWipe(w: Workspace, itemId: string, reason: string, now = new Date()): Workspace {
  const why = reason.trim()
  const prev = w.checks[itemId] ?? { done: false }
  const next: Workspace = { ...w, updatedAt: now.toISOString(), checks: { ...w.checks, [itemId]: { ...prev, done: true, doneAt: now.toISOString(), declined: true, note: why } } }
  return addLog(next, `Decided not to wipe the device${why ? `: ${why}` : ""}.`, now)
}

/** A short message to send the assignee. Deliberately contains no incident details. */
export function assigneeMessage(a: Assignment, now = new Date()): string {
  const due = a.dueAt ? ` Please have it done by ${new Date(a.dueAt).toLocaleString()}.` : " Please do it as soon as you can."
  return `Hi ${a.assigneeName || "there"}, I need your help with something urgent and confidential: ${a.title}.${a.note ? ` ${a.note}` : ""}${due} Please tell me the moment it is done, and the time you did it, so I can record it. Please don't discuss it outside our group or in email or chat that others can read. Thank you. (Sent ${now.toLocaleString()}.)`
}
